import { FilterOutlined } from '@ant-design/icons'
import { flatNodes, type ViewConfigField, type ViewConfigFilterNode, type ViewConfigSheet } from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { addModelIdProperty, useBooleanState } from '@salescore/frontend-common'
import { Col, Row, Space, Switch } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'

import type { ViewConfigFilterNodeForForm } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/filter/common'
import {
  applyAdvancedSettingsFlag,
  ViewFilterTreeForm,
} from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/filter/ViewFilterTreeForm'
import { useGetModel } from '../../../recoil/view/selectors/connectionsSelector'

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function MeasureFilterForm({
  sheetConfig,
  originalField,
  onChange,
  initialFilterTree,
}: {
  sheetConfig: ViewConfigSheet
  originalField: ViewConfigField
  onChange: (filterTree: ViewConfigFilterNode | undefined) => void
  initialFilterTree: ViewConfigFilterNodeForForm | undefined
}) {
  const getModel = useGetModel()
  // eslint-disable-next-line sonarjs/deprecation
  const isAdvancedModeState = useBooleanState(
    (initialFilterTree?.children ?? []).length > 0 || initialFilterTree?.logicalOperator === 'or',
  )
  const flattenNodes = flatNodes(sheetConfig.tree)
  const getNode = (nodeName: string) => flattenNodes.find((x) => x.name === nodeName)
  const originalNode = getNode(originalField.property.nodeName)
  const originalModel = getModel(originalField.property.modelName)
  const propertiesWithNode = (sheetConfig.fields ?? [])
    .map((field) => {
      const { nodeName, modelName, propertyName } = field.property
      if (nodeName !== originalNode?.name) {
        // 重複レコードの回避処理周りの都合で、いったん同じノードのフィールドのみに限定する
        // この処理をなくすためには、pruneの処理と、case whenをやめてfilterに統合するような処理が必要
        // eslint-disable-next-line array-callback-return
        return
      }
      const node = getNode(nodeName)
      const model = getModel(modelName)
      const property = model?.properties.find((x) => x.name === propertyName)
      if (node === undefined || model === undefined || property === undefined) {
        // eslint-disable-next-line array-callback-return
        return
      }
      return {
        node,
        model,
        property,
      }
    })
    .filter(isNotNullish)
  const propertiesAndOriginalIdWithNode = addModelIdProperty(
    { node: originalNode, model: originalModel },
    propertiesWithNode,
  )

  return (
    <div>
      <Row justify="space-between" className="mb-4">
        <Col>
          <Space className="font-normal">
            <FilterOutlined />
            <span>{t(`集計条件`)}</span>
          </Space>
        </Col>
        <Col>
          <Space>
            {t(`高度なフィルタ条件`)}
            <Switch
              checked={isAdvancedModeState.isTrue}
              onChange={(value) => {
                if (value) {
                  isAdvancedModeState.setTrue()
                } else {
                  isAdvancedModeState.setFalse()
                }
              }}
            />
          </Space>
        </Col>
      </Row>
      <ViewFilterTreeForm
        filterTree={initialFilterTree}
        // 内部でDividerWithLogicalOperatorにclass: ant-form-horizontalがあたってしまうことがあるためのWorkaround
        inNestedForm
        propertiesWithNode={propertiesAndOriginalIdWithNode}
        isAdvancedMode={isAdvancedModeState.isTrue}
        onChange={(filterTree) => {
          const newFilterTree = applyAdvancedSettingsFlag(isAdvancedModeState.isTrue, filterTree)
          onChange(newFilterTree)
        }}
      />
    </div>
  )
}
